<template>
  <BaseFormLayout
    :title="
      editMode ? `Edit ${fleetModule.vehicle}` : `Add ${fleetModule.vehicle}`
    "
    :nonFieldErrors="formErrors.non_field_errors"
  >
    <template #actions>
      <SecondaryButton
        v-if="!editMode"
        @click="resetForm()"
        btnClass="mr-2 text-uppercase rounded-lg"
        name="Reset"
      >
        Reset
      </SecondaryButton>
      <PrimaryButton
        @click="submitForm()"
        :disabled="!isValid"
        btnClass="text-uppercase rounded-lg"
        name="Submit"
      >
      </PrimaryButton>
    </template>
    <template #content>
      <v-form
        v-model="isValid"
        ref="vehicleForm"
        :key="formCount"
        @submit.prevent="submitForm"
      >
        <v-row class="ma-0 pa-0">
          <v-col cols="12" lg="8" class="pa-0 pr-lg-2">
            <v-card elevation="0" class="rounded-lg pa-4">
              <v-row no-gutters>
                <v-col cols="12" lg="4" class="d-flex justify-center pa-4">
                  <v-card outlined class="pa-8">
                    <v-icon size="100" color="primary">mdi-truck</v-icon>
                  </v-card>
                </v-col>
                <v-col cols="12" lg="8">
                  <v-row class="ma-0">
                    <v-col cols="12">
                      <span class="text-subtitle-1 font-weight-bold"
                        >{{ fleetModule.vehicle }} Information</span
                      >
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        v-model="vehicleDetails.vehicle_number"
                        :label="`${fleetModule.vehicle} Number`"
                        name="vehicle_number"
                        :rules="[
                          (val) =>
                            (val && vehicleNoValidation(val)) ||
                            'No special characters are allowed',
                        ]"
                        :required="true"
                        :error-messages="formErrors.vehicle_number"
                        @input="delete formErrors.vehicle_number"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <SelectField
                        v-model="vehicleDetails.storage_types"
                        :itemsList="storageTypeList"
                        itemText="name"
                        name="storage_types"
                        itemValue="id"
                        :multiple="true"
                        label="Storage Type *"
                        :rules="[
                          (val) =>
                            (!!val && val.length > 0) ||
                            'Storage Type is required',
                        ]"
                        :error-messages="formErrors.storage_types"
                        @input="delete formErrors.storage_types"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <SelectField
                        v-model="vehicleDetails.fuel_type"
                        :itemsList="fuelTypeList"
                        name="fuel_type"
                        label="Fuel Type*"
                        :rules="[(val) => !!val || 'Fuel Type is required']"
                        :error-messages="formErrors.fuel_type"
                        @input="delete formErrors.fuel_type"
                      />
                    </v-col>
                    <v-col cols="12" lg="4">
                      <InputField
                        type="number"
                        v-model="vehicleDetails.odometer_reading"
                        name="odometer_reading"
                        label="Odometer"
                        :required="true"
                        :error-messages="formErrors.odometer_reading"
                        @input="delete formErrors.odometer_reading"
                      />
                    </v-col>

                    <v-col cols="12" lg="4" class="d-flex align-center">
                      <SwitchField
                        v-model="vehicleDetails.is_active"
                        inset
                        name="is_active"
                        :label="`${
                          vehicleDetails.is_active ? 'Active' : 'Deactivated'
                        }`"
                        :error-messages="formErrors.is_active"
                        @input="delete formErrors.is_active"
                      ></SwitchField>
                    </v-col>
                    <v-col cols="12" lg="4" v-if="!vehicleDetails.is_active">
                      <InputField
                        v-model="vehicleDetails.deactivation_remarks"
                        label="Deactivation Remarks"
                        name="deactivation_remarks"
                        :required="true"
                        :error-messages="formErrors.deactivation_remarks"
                        @input="delete formErrors.deactivation_remarks"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card>
            <v-card elevation="0" class="rounded-lg px-4 pt-4 pb-8 mt-4">
              <v-row class="ma-0">
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold"
                    >Other Information</span
                  >
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    type="number"
                    v-model="vehicleDetails.tonnage_capacity"
                    min="0"
                    name="tonnage_capacity"
                    label="Tonnage Capacity"
                    :required="true"
                    :error-messages="formErrors.tonnage_capacity"
                    @input="delete formErrors.tonnage_capacity"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    type="number"
                    v-model="vehicleDetails.cbm_capacity"
                    name="cbm_capacity"
                    min="0"
                    label="CBM Capacity"
                    :required="true"
                    :error-messages="formErrors.cbm_capacity"
                    @input="delete formErrors.cbm_capacity"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    v-model="vehicleDetails.vehicle_make"
                    :label="`${fleetModule.vehicle} Make`"
                    name="vehicle_make"
                    :error-messages="formErrors.vehicle_make"
                    @input="delete formErrors.vehicle_make"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    v-model="vehicleDetails.vehicle_model"
                    :label="`${fleetModule.vehicle} Model`"
                    name="vehicle_model"
                    :error-messages="formErrors.vehicle_model"
                    @input="delete formErrors.vehicle_model"
                  />
                </v-col>
                <v-col cols="12" lg="4">
                  <InputField
                    type="number"
                    min="1880"
                    name="vehicle_year"
                    v-model="vehicleDetails.vehicle_year"
                    :label="`${fleetModule.vehicle} Year`"
                    :error-messages="formErrors.vehicle_year"
                    @input="delete formErrors.vehicle_year"
                  />
                </v-col>
                <v-col
                  cols="12"
                  lg="4"
                  v-if="
                    userDetails &&
                    userDetails.company_details &&
                    userDetails.company_details.configuration &&
                    userDetails.company_details.configuration
                      .has_telematics_device
                  "
                >
                  <InputField
                    name="vehicle_year"
                    v-model="vehicleDetails.telematics_device_id"
                    :label="`Telematics Device Id`"
                    :error-messages="formErrors.telematics_device_id"
                    @input="delete formErrors.telematics_device_id"
                  />
                </v-col>
                <v-col cols="12" lg="4" class="d-flex">
                  <InputField
                    type="number"
                    min="0"
                    class="mr-1"
                    name="vehicle_cost"
                    v-model="vehicleDetails.vehicle_cost"
                    :label="`${fleetModule.vehicle} Cost`"
                    :error-messages="formErrors.vehicle_cost"
                    @input="delete formErrors.vehicle_cost"
                  />
                  <SelectField
                    v-model="vehicleDetails.currency"
                    :itemsList="currencyList"
                    name="currency"
                    style="width: 200px"
                    itemText="code"
                    itemValue="id"
                    :label="`currency`"
                    :isSearchRequired="false"
                    :error-messages="formErrors.currency"
                    @input="delete formErrors.currency"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
          <v-col cols="12" lg="4" class="pa-0 pt-4 pl-lg-2 pt-lg-0">
            <v-card elevation="0" class="rounded-lg pa-4 h-100">
              <v-row class="ma-0">
                <v-col cols="12" class="py-12 d-flex justify-center">
                  <v-card outlined class="pa-8">
                    <v-icon size="100" color="primary">mdi-warehouse</v-icon>
                  </v-card>
                </v-col>
                <v-col cols="12">
                  <span class="text-subtitle-1 font-weight-bold"
                    >Basic Information</span
                  >
                </v-col>
                <v-col cols="12" lg="6">
                  <SelectField
                    v-model="vehicleDetails.branch"
                    :itemsList="branchList"
                    name="branch"
                    item-text="name"
                    item-value="id"
                    :label="`${configurationModule.branch}*`"
                    :rules="[
                      (val) =>
                        !!val || `${configurationModule.branch} is required`,
                    ]"
                    :error-messages="formErrors.branch"
                    @input="delete formErrors.branch"
                    @change="getSelectBranch($event)"
                  />
                </v-col>
                <v-col cols="12" lg="6">
                  <SelectField
                    v-model="vehicleDetails.project"
                    :itemsList="projectList"
                    name="project"
                    item-text="name"
                    item-value="id"
                    :label="`${configurationModule.project}*`"
                    :required="true"
                    :rules="[
                      (val) =>
                        !!val || `${configurationModule.project} is required`,
                    ]"
                    :error-messages="formErrors.project"
                    @input="delete formErrors.project"
                  />
                </v-col>
                <v-col cols="12" lg="12">
                  <SelectField
                    v-model="vehicleDetails.tags"
                    :label="`${tripPlanningModule.tagPlural}`"
                    itemValue="id"
                    itemText="name"
                    name="tags"
                    :multiple="true"
                    :itemsList="vehicleTagList"
                    :error-messages="formErrors.tags"
                    @input="delete formErrors.tags"
                  />
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
        <v-row
          class="mt-4 mt-lg-0"
          v-if="customOptionFieldList && customOptionFieldList.length > 0"
        >
          <v-col cols="12">
            <CustomFieldRenderForm
              :fieldList="customOptionFieldList"
              :formObject="customObject"
              :fieldsErrors="customFieldsErrors"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </BaseFormLayout>
</template>

<script>
import { bus } from "@/main.js";
import BaseFormLayout from "@/components/LayoutComponents/BaseFormLayout.vue";
import {
  configurationModule,
  fleetModule,
  tripPlanningModule,
} from "@/utils/moduleMapping";

import { customFieldDefaultData } from "@/utils/functions";

export default {
  components: {
    BaseFormLayout,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    editMode: {
      required: true,
    },
  },
  data() {
    return {
      customFieldDefaultData,
      tripPlanningModule,
      fleetModule,
      configurationModule,
      fileInput: null,
      formCount: 0,
      isValid: true,
      vehicleDetails: {
        is_active: true,
        tags: [],
      },
      formErrors: {},

      branchList: [],
      currencyList: [],
      vehicleTagList: [],
      storageTypeList: [],
      fuelTypeList: [],

      projectList: [],
      customOptionFieldList: [],
      customFieldsErrors: {},
      customObject: {},
      userDetails: this.$helper.getUserDetailsLocalStorage(),
    };
  },
  computed: {
    selectedBranch() {
      let obj = this.$helper.getUserDetailsLocalStorage();
      if (obj) {
        return obj.preferred_branch.id;
      } else {
        return null;
      }
    },
  },
  methods: {
    vehicleNoValidation(string) {
      let validRegex = /^[a-zA-Z0-9_-]+$/i;
      return validRegex.test(string);
    },
    resetForm() {
      this.$refs.vehicleForm.resetValidation();
      this.vehicleDetails = {
        is_active: true,
        tags: [],
        branch: this.selectedBranch,
      };

      this.getVehicleFormOptions();
    },
    getSelectBranch(id) {
      if (id) {
        this.getProjectList(id);
        this.getBranchDetail(id);
      }
    },
    getBranchDetail(id) {
      this.$api.branch
        .getBranchDetail(id)
        .then((res) => {
          if (this.vehicleDetails.vehicle_cost) {
            this.vehicleDetails = {
              ...this.vehicleDetails,
              currency: res.data.currency,
            };
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getVehicleObject(id) {
      bus.$emit("showLoader", true);
      this.$api.vehicle
        .getVehicleObject(id)
        .then((res) => {
          bus.$emit("showLoader", false);
          this.vehicleDetails = res.data;
          if (
            res &&
            res.data &&
            res.data.custom_fields &&
            Object.keys(res.data.custom_fields).length > 0
          ) {
            this.customObject = res?.data?.custom_fields;
          }
          this.getProjectList(this.vehicleDetails.branch);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    getVehicleTagList(params = {}) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        limit: "all",
      };
      this.$api.tag
        .getTagList(params)
        .then((res) => {
          this.vehicleTagList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    getFieldConfiguration(params = {}, objKey) {
      bus.$emit("showLoader", true);
      params = {
        ...params,
        limit: "all",
      };
      this.$api.fieldConfiguration
        .getFieldConfigurationList(params)
        .then((res) => {
          this[objKey] = res.data.map((obj) => obj.name);
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    submitForm() {
      if (!this.$refs.vehicleForm.validate()) {
        bus.$emit("showToastMessage", {
          message: "Please correct all the errors to submit the form!",
          color: "error",
        });
        return;
      }

      let data = {
        ...this.vehicleDetails,
        custom_fields: { ...this.customObject },
      };

      if (!data.vehicle_cost) {
        delete data.vehicle_cost;
        delete data.currency;
      }
      bus.$emit("showLoader", true);
      if (this.editMode) {
        this.$api.vehicle
          .updateVehicleObject({
            id: this.vehicleDetails.id,
            data: data,
          })
          .then(() => {
            bus.$emit("showLoader", false);
            bus.$emit("showToastMessage", {
              message: "Vehicle information updated!",
              color: "success",
            });
            this.resetForm();
            this.$router.back();
          })
          .catch((err) => {
            bus.$emit("showLoader", false);
            this.formErrors = err.data;
            this.customFieldsErrors = err?.data?.custom_fields;
          });
      } else {
        this.$api.vehicle
          .createVehicleObject(data)
          .then(() => {
            bus.$emit("showLoader", false);
            bus.$emit("showToastMessage", {
              message: "Vehicle created!",
              color: "success",
            });
            this.resetForm();
            this.$router.back();
          })
          .catch((err) => {
            bus.$emit("showLoader", false);
            this.formErrors = err.data;
            this.customFieldsErrors = err?.data?.custom_fields;
          });
      }
    },
    getBranchList() {
      bus.$emit("showLoader", true);
      this.$api.branch
        .getBranchSelectList({ limit: "all" })
        .then((res) => {
          this.branchList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    getCurrencyList() {
      bus.$emit("showLoader", true);
      this.$api.currency
        .getCurrencyList({ limit: "all" })
        .then((res) => {
          this.currencyList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    getVehicleOptions() {
      bus.$emit("showLoader", true);
      return this.$api.vehicle
        .getVehicleOption({ request_type: "filter" })
        .then((res) => {
          this.storageType = res?.data?.storage_type;

          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getVehicleFormOptions() {
      bus.$emit("showLoader", true);
      return this.$api.vehicle
        .getVehicleOption({ request_type: "form" })
        .then((res) => {
          this.customOptionFieldList = res?.data?.custom_fields;
          this.customObject = this.customFieldDefaultData(
            this.customOptionFieldList
          );
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getStorageTypeSelectList() {
      bus.$emit("showLoader", true);
      return this.$api.storageType
        .getStorageTypeSelectList({ limit: "all" })
        .then((res) => {
          this.storageTypeList = res.data;
          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.log(err);
          bus.$emit("showLoader", false);
        });
    },
    getProjectList(id) {
      if (id) {
        this.$api.project
          .getProjectSelectList({ limit: "all", branch: id })
          .then((res) => {
            this.projectList = res.data;
            let defaultProject = this.projectList.find((v) => v.is_default);

            if (
              this.vehicleDetails &&
              this.vehicleDetails.project &&
              this.editMode
            ) {
              this.vehicleDetails.project = this.vehicleDetails.project;
            } else {
              this.vehicleDetails.project = defaultProject?.id;
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        this.projectList = [];
      }
    },
  },
  async mounted() {
    // await this.getVehicleOptions();
    await this.getVehicleFormOptions();
    await this.getBranchList();
    if (this.selectedBranch && !this.editMode) {
      this.vehicleDetails.branch = this.selectedBranch;
    }
    if (this.selectedBranch) {
      this.getSelectBranch(this.selectedBranch);
    }
    this.getVehicleTagList();
    this.getCurrencyList();
    this.getStorageTypeSelectList();
    this.getFieldConfiguration({ search: "fuel type" }, "fuelTypeList");
    if (this.$route && this.$route.params && this.$route.params.id) {
      this.getVehicleObject(this.$route.params.id);
    }
  },
};
</script>

<style>
.v-select__slot label {
  z-index: 2;
}
.Map-Height {
  height: calc(100vh - 300px);
}
</style>
